import { useMemo } from 'react'

import { BarChart } from '@/components/charts/bar-chart.tsx'
import { Card, CardContent } from '@/components/ui/card.tsx'
import type { AccountingSnapshotItem } from '@/modules/accounting/types.ts'
import { roundAmount } from '@/utils/number.ts'
import { useIsDarkTheme } from '@/utils/useTheme.tsx'

interface Props {
  accountingSnapshotItems: AccountingSnapshotItem[]
  showCents?: boolean
}

export const AccountingCostsChart = ({
  accountingSnapshotItems,
  showCents = true,
}: Props) => {
  const isDarkMode = useIsDarkTheme()

  const {
    totalCreatorCosts,
    totalMemberCosts,
    totalAdditionalCosts,
    labels,
    creatorData,
    memberData,
    additionalCostData,
  } = useMemo(() => {
    return accountingSnapshotItems.reduce(
      (acc, item) => {
        acc.totalCreatorCosts += item.creatorCosts
        acc.totalMemberCosts += item.memberCosts
        acc.totalAdditionalCosts += item.additionalCosts
        acc.labels.push(item.date)
        acc.creatorData.push(item.creatorCosts)
        acc.memberData.push(item.memberCosts)
        acc.additionalCostData.push(item.additionalCosts)

        return acc
      },
      {
        totalCreatorCosts: 0,
        totalMemberCosts: 0,
        totalAdditionalCosts: 0,
        labels: [] as string[],
        creatorData: [] as number[],
        memberData: [] as number[],
        additionalCostData: [] as number[],
      },
    )
  }, [accountingSnapshotItems])

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Creators',
          data: creatorData,
          backgroundColor: isDarkMode ? '#300B1C' : '#FCD7E8',
          borderColor: '#EF358E',
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.65,
          categoryPercentage: 0.55,
        },
        {
          label: 'Members',
          data: memberData,
          backgroundColor: isDarkMode ? '#2A2619' : '#FFF9E5',
          borderColor: '#FFB800',
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.65,
          categoryPercentage: 0.55,
        },
        {
          label: 'Additional Costs',
          data: additionalCostData,
          backgroundColor: isDarkMode ? '#022829' : '#CEF4F5',
          borderColor: '#0BC9CD',
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.65,
          categoryPercentage: 0.55,
        },
      ],
    }),
    [labels, creatorData, isDarkMode, memberData, additionalCostData],
  )

  return (
    <Card>
      <CardContent className="mt-6">
        <div className="grid grid-cols-3 gap-3">
          <div className="flex items-center justify-between rounded border-l-4 border-pink-500 bg-pink-50 px-4 py-2 dark:bg-pink-950">
            <div>
              <span className="text-text-default font-medium">
                {roundAmount(totalCreatorCosts, showCents)}
              </span>
              <p className="text-secondary-300 text-sm">Creator Costs</p>
            </div>
          </div>

          <div className="border-warning-default bg-warning-light flex items-center justify-between rounded border-l-4 px-4 py-2 dark:bg-[#2A2619]">
            <div>
              <span className="text-text-default font-medium">
                {roundAmount(totalMemberCosts, showCents)}
              </span>
              <p className="text-secondary-300 text-sm">Member Costs</p>
            </div>
          </div>

          <div className="flex items-center justify-between rounded border-l-4 border-teal-500 bg-teal-50 px-4 py-2 dark:bg-teal-900">
            <div>
              <span className="text-text-default font-medium">
                {roundAmount(totalAdditionalCosts, showCents)}
              </span>
              <p className="text-secondary-300 text-sm">Additional Costs</p>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <BarChart
            toggleOptions={{ isTooltipCurrency: true }}
            showCents={showCents}
            data={data}
          />
        </div>
      </CardContent>
    </Card>
  )
}
